import { useState, useEffect } from 'react';
import { useAppContext } from '../store/AppContext';
import { saveUnitSystem, getUnitPreferences, saveUnitPreferences, type UnitPreferences } from '../services/userProfile.service';
import type { UnitSystem } from '../utils/unitConversions';

const UNIT_CACHE_KEY = 'coretrak.unitPreferences';

function getCachedUnits(): UnitPreferences {
  try {
    const cached = localStorage.getItem(UNIT_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.body && parsed.food) return parsed;
    }
  } catch {}
  return { body: 'metric', food: 'metric' };
}

function cacheUnits(prefs: UnitPreferences) {
  try {
    localStorage.setItem(UNIT_CACHE_KEY, JSON.stringify(prefs));
  } catch {}
}

export function useUnitSystem() {
  const { user } = useAppContext();
  const [units, setUnits] = useState<UnitPreferences>(getCachedUnits);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>(getCachedUnits().body);
  // Only start in loading state if no cached units exist
  const [loading, setLoading] = useState(() => {
    try {
      return !localStorage.getItem(UNIT_CACHE_KEY);
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    const userId = user.uid;
    async function loadUnitSystem() {
      try {
        const preferences = await getUnitPreferences(userId);
        setUnits(preferences);
        setUnitSystem(preferences.body);
        cacheUnits(preferences);
      } catch (error) {
        console.error('Failed to load unit system:', error);
      } finally {
        setLoading(false);
      }
    }
    loadUnitSystem();
  }, [user]);

  async function updateUnitSystem(newSystem: UnitSystem) {
    if (!user) return;
    try {
      await saveUnitSystem(user.uid, newSystem);
      const updatedUnits = { body: newSystem, food: newSystem };
      setUnits(updatedUnits);
      setUnitSystem(newSystem);
      cacheUnits(updatedUnits);
    } catch (error) {
      console.error('Failed to save unit system:', error);
      throw error;
    }
  }

  async function updateUnitPreferences(newUnits: UnitPreferences) {
    if (!user) return;
    try {
      await saveUnitPreferences(user.uid, newUnits);
      setUnits(newUnits);
      setUnitSystem(newUnits.body);
      cacheUnits(newUnits);
    } catch (error) {
      console.error('Failed to save unit preferences:', error);
      throw error;
    }
  }

  return {
    unitSystem,
    units,
    bodyUnits: units.body,
    foodUnits: units.food,
    updateUnitSystem,
    updateUnitPreferences,
    loading,
  };
}